import React from 'react';
import DOMPurify from 'dompurify';
import { Badge, Box, Flex, Text, Heading, IconButton, Icon } from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  Bars3Icon,
  EyeIcon,
  EyeSlashIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/tooltip';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu';
import { Collection } from '@/lib/types';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';

type Props = {
  item: Collection;
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  onView: (item: Collection) => void;
  onGeneratePiblicLink: (item: Collection) => void;
  onDuplicate: (item: Collection) => void;
  onEdit: (item: Collection) => void;
  onDelete: (item: Collection) => void;
  onChangeStatus: (item: Collection, status: 'draft' | 'published') => void;
};

const ItemCard: React.FC<Props> = ({
  item,
  lastContentElementRef,
  onView,
  onGeneratePiblicLink,
  onDuplicate,
  onEdit,
  onDelete,
  onChangeStatus,
}) => {
  const isAdmin = useIsInAdminGroup();

  function stripHtmlTags(htmlString: string) {
    return DOMPurify.sanitize(htmlString, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }

  return (
    <Box
      key={item.reference_id}
      ref={lastContentElementRef}
      bg='bg.panel'
      border='1px solid'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      _hover={{ shadow: 'md' }}
      transition='box-shadow 0.15s ease'
    >
      <Flex alignItems='flex-start'>
        {/* Clickable content area */}
        <Box
          flex={1}
          p={4}
          cursor='pointer'
          onClick={() => onView(item)}
        >
          <Flex gap={2} mb={2} wrap='wrap'>
            {/* Intentional fixed status badge (entity type), NOT a field tag — slot rule does not apply */}
            <Badge bg='slot1.bg' color='slot1.fg' size='sm'>
              Collection
            </Badge>
            {item.status === 'published' && (
              /* Intentional fixed status badge (publish state), NOT a field tag — slot rule does not apply */
              <Badge bg='slot3.bg' color='slot3.fg' size='sm'>
                Published
              </Badge>
            )}
            {item.status === 'draft' && (
              /* Intentional fixed status badge (draft state), NOT a field tag — slot rule does not apply */
              <Badge bg='bg.subtle' color='fg.muted' size='sm'>
                Draft
              </Badge>
            )}
          </Flex>
          <Heading as='h3' size='sm' color='fg.strong' lineHeight={1.4} mb={1}>
            {item.name}
          </Heading>
          {item.description && (
            <Text fontSize='xs' color='fg.muted' lineClamp={3} mb={1}>
              {stripHtmlTags(item.description)}
            </Text>
          )}
          <Text fontSize='xs' color='fg.subtle'>
            {new Date(item.created_at).toLocaleDateString()}
          </Text>
        </Box>

        {/* Admin action buttons */}
        <Box px={2} py={3}>
          <Box display={{ base: 'none', md: 'flex' }} gap={0}>
            {isAdmin && (
              <>
                <Tooltip
                  content={`${item.status === 'published' ? 'Hide from workspace' : 'Share to workspace'}`}
                >
                  <IconButton
                    variant='ghost'
                    aria-label={`${item.status}`}
                    size='sm'
                    onClick={() =>
                      onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                    }
                  >
                    <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content='Share public link'>
                  <IconButton
                    variant='ghost'
                    aria-label='Share public link'
                    size='sm'
                    onClick={() => onGeneratePiblicLink(item)}
                  >
                    <Icon as={ShareIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content='Duplicate'>
                  <IconButton
                    variant='ghost'
                    aria-label='Duplicate'
                    size='sm'
                    onClick={() => onDuplicate(item)}
                  >
                    <Icon as={DocumentDuplicateIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content='Edit'>
                  <IconButton
                    variant='ghost'
                    aria-label='Edit'
                    size='sm'
                    onClick={() => onEdit(item)}
                  >
                    <Icon as={PencilIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content='Delete'>
                  <IconButton
                    variant='ghost'
                    aria-label='Delete'
                    size='sm'
                    onClick={() => onDelete(item)}
                  >
                    <Icon as={TrashIcon} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          <Box display={{ base: 'block', md: 'none' }}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton variant='outline' aria-label='Menu' size='sm'>
                  <Icon as={Bars3Icon} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                {isAdmin && (
                  <>
                    <MenuItem
                      value='change-status'
                      onClick={() =>
                        onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                      }
                    >
                      <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} w={4} h={4} />
                      {item.status === 'published' ? 'Hide from workspace' : 'Share to workspace'}
                    </MenuItem>
                    <MenuItem value='share-public-link' onClick={() => onGeneratePiblicLink(item)}>
                      <Icon as={ShareIcon} w={4} h={4} />
                      Share public link
                    </MenuItem>
                    <MenuItem value='duplicate' onClick={() => onDuplicate(item)}>
                      <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                      Duplicate
                    </MenuItem>
                    <MenuItem value='edit' onClick={() => onEdit(item)}>
                      <Icon as={PencilIcon} w={4} h={4} />
                      Edit
                    </MenuItem>
                    <MenuSeparator />
                    <MenuItem value='delete' onClick={() => onDelete(item)} color='danger'>
                      <Icon as={TrashIcon} w={4} h={4} />
                      Delete
                    </MenuItem>
                  </>
                )}
              </MenuContent>
            </MenuRoot>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default ItemCard;
